import { useEffect, useState } from 'react';
import invitationService from '../../services/invitation.service';
import { SurveyInvitation, DeliveryStatus } from '../../types/invitation.types';

// Phase 5 - US3: Invitation Status Component (T100)

interface InvitationStatusProps {
  surveyId: string;
}

const InvitationStatus = ({ surveyId }: InvitationStatusProps) => {
  const [invitations, setInvitations] = useState<SurveyInvitation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<DeliveryStatus | undefined>();
  const limit = 20;

  useEffect(() => {
    loadInvitations();
  }, [surveyId, page, filterStatus]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const result = await invitationService.listInvitations(surveyId, {
        deliveryStatus: filterStatus,
        page,
        limit,
      });
      setInvitations(result.invitations);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load invitations', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: DeliveryStatus) => {
    const colors = {
      [DeliveryStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [DeliveryStatus.SENT]: 'bg-blue-100 text-blue-800',
      [DeliveryStatus.DELIVERED]: 'bg-green-100 text-green-800',
      [DeliveryStatus.FAILED]: 'bg-red-100 text-red-800',
      [DeliveryStatus.BOUNCED]: 'bg-orange-100 text-orange-800',
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status]}`}
      >
        {status}
      </span>
    );
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Telefone', 'Status', 'Data de Envio', 'Erro'];
    const rows = invitations.map((inv) => [
      inv.recipientEmail || '',
      inv.recipientPhone || '',
      inv.deliveryStatus,
      inv.sentAt ? new Date(inv.sentAt).toLocaleString('pt-BR') : '',
      inv.errorMessage || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invitations_${surveyId}_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && page === 1) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Convites Enviados</h2>
        <div className="flex gap-2">
          <button
            onClick={loadInvitations}
            disabled={loading}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            🔄 Atualizar
          </button>
          <button
            onClick={exportToCSV}
            disabled={invitations.length === 0}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            📥 Exportar CSV
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 mr-2">Filtrar por status:</label>
        <select
          value={filterStatus || ''}
          onChange={(e) => {
            setFilterStatus(e.target.value as DeliveryStatus | undefined);
            setPage(1);
          }}
          className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos</option>
          <option value={DeliveryStatus.PENDING}>Pendente</option>
          <option value={DeliveryStatus.SENT}>Enviado</option>
          <option value={DeliveryStatus.DELIVERED}>Entregue</option>
          <option value={DeliveryStatus.FAILED}>Falha</option>
          <option value={DeliveryStatus.BOUNCED}>Rejeitado</option>
        </select>
      </div>

      {/* Table */}
      {invitations.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nenhum convite encontrado
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destinatário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Envio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erro
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {invitation.recipientEmail && (
                        <div>{invitation.recipientEmail}</div>
                      )}
                      {invitation.recipientPhone && (
                        <div className="text-gray-500">
                          {invitation.recipientPhone}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(invitation.deliveryStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.sentAt
                        ? new Date(invitation.sentAt).toLocaleString('pt-BR')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600">
                      {invitation.errorMessage && (
                        <div className="max-w-xs truncate" title={invitation.errorMessage}>
                          {invitation.errorMessage}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Mostrando {(page - 1) * limit + 1} a {Math.min(page * limit, total)} de {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-1">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvitationStatus;
